import UAParser from 'ua-parser-js'
import { v4 as uuid } from 'uuid'

import AuthUtils from 'utils/AuthUtils'

import type { AnalyticsEvent, BrowserDetails } from './types'

export const addIdToEvents = (events: AnalyticsEvent[]) => {
  return events?.map((event) => {
    return {
      id: uuid(),
      ...event
    }
  })
}

export const extractBaseUrlForExternalLink = (url: string) => {
  try {
    const urlWithoutParams = new URL(url)
    const baseUrl = urlWithoutParams?.origin + urlWithoutParams?.pathname

    return baseUrl
  } catch (err) {
    return null
  }
}

export const fetchBrowserDetails = (): BrowserDetails => {
  const uaParser = new UAParser()

  uaParser.setUA(navigator.userAgent)

  const userAgent = uaParser.getResult()

  const rawDeviceType = userAgent.device.type
  const deviceType = rawDeviceType
    ? `${rawDeviceType[0].toUpperCase()}${rawDeviceType.slice(1)}`
    : 'Desktop'

  const osType = userAgent.os.name

  const browserType = userAgent.browser.name

  const browserVersion = {
    major: userAgent.browser.major,
    full: userAgent.browser.version
  }

  const resolution = {
    width: window.screen.width,
    height: window.screen.height
  }

  const viewport = {
    width: window.innerWidth,
    height: window.innerHeight
  }

  const colorDepth = screen.colorDepth
  const languagePreference = navigator.language

  return {
    deviceType,
    osType,
    browserType,
    browserVersion,
    resolution,
    viewport,
    colorDepth,
    languagePreference
  }
}

export const retrieveStoredUtmParams = () => {
  const storedUtm = localStorage.getItem('utm')

  return storedUtm ? JSON.parse(storedUtm) : {}
}

const fetchUtmParams = () => {
  const urlParams = new URLSearchParams(window.location.search)

  const utmParams = Object.fromEntries(
    [...urlParams.entries()].filter(([key]) => key.startsWith('utm_'))
  )

  return utmParams
}

/**
 * Stores referrer and utm params in localStorage for session tracking
 *
 * Store it in localStorage if it’s not already set (to avoid overwriting during login page refresh)
 * And add it to analytics session when user logs in
 */
export const trackSource = () => {
  const referrer = document.referrer

  if (!localStorage.getItem('referrer') && referrer) {
    localStorage.setItem('referrer', referrer)
  }

  const utm = fetchUtmParams()

  if (!localStorage.getItem('utm') && Object.keys(utm).length) {
    localStorage.setItem('utm', JSON.stringify(utm))
  }
}

export const getAccessToken = async () => {
  try {
    const token = await AuthUtils.getAccessToken()

    return token || localStorage.getItem('publicToken')
  } catch (err) {
    return null
  }
}

export const getCurrrentSession = () => {
  try {
    const session = JSON.parse(localStorage.getItem('session'))

    return session
  } catch (err) {
    return {}
  }
}

export const userActivityEvents = {
  addContractInitiated: 'add_contract_initiated',
  additionalInfoUpdateInitiated: 'additional_info_update_initiated',
  additionalInfoUpdated: 'additional_info_updated',
  additionalInfoViewed: 'additional_info_viewed',
  allDocumentsDownloaded: 'all_documents_downloaded',
  contractAdded: 'contract_added',
  contractDueDateChanged: 'contract_due_date_changed',
  contractPaymentRateChanged: 'contract_payment_rate_changed',
  contractsListingViewed: 'contracts_listing_viewed',
  contractViewed: 'contract_viewed',
  documentDeleted: 'document_deleted',
  documentDownloaded: 'document_downloaded',
  documentUploaded: 'document_uploaded',
  documentsListingViewed: 'documents_listing_viewed',
  externalWebsiteOpened: 'external_website_opened',
  journeyOpened: 'journey_opened',
  journeySideBarReferrer: 'sidebar',
  journeyWorkflowReferrer: 'workflow',
  meterReadingSubmissionInitiated: 'meter_reading_submission_initiated',
  meterReadingSubmitted: 'meter_reading_submitted',
  metersListingViewed: 'meters_listing_viewed',
  meterViewed: 'meter_viewed',
  meterWidgetViewed: 'meter_widget_viewed',
  opportunitiesListingViewed: 'opportunities_listing_viewed',
  opportunityViewed: 'opportunity_viewed',
  orderAccepted: 'order_accepted',
  orderRefused: 'order_refused',
  orderViewed: 'order_viewed',
  ordersListingViewed: 'orders_listing_viewed',
  pageNavigation: 'page_navigation',
  passwordChanged: 'password_changed',
  paymentsListingViewed: 'payments_listing_viewed',
  productViewed: 'product_viewed',
  teaserOpened: 'teaser_opened',
  userAccountDeleted: 'user_account_deleted',
  userEmailChanged: 'user_email_changed',
  userLoggedIn: 'user_logged_in',
  userLoggedOut: 'user_logged_out',
  userRegistration: 'user_registered'
}

export const schemaToEventMap = {
  contract: userActivityEvents.contractViewed,
  meter: userActivityEvents.meterViewed,
  opportunity: userActivityEvents.opportunityViewed,
  order: userActivityEvents.orderViewed
}
